import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useI18n } from '../i18n/index.ts';

const SHEET_ROOT = '.panel-sheet, .learning-popup--sheet';
const DISMISS_PX = 72;

type SheetHandleProps = {
  onDismiss: () => void;
  className?: string;
  testId?: string;
};

function sheetBaseTransform(sheet: HTMLElement): string {
  const dim = sheet.closest('.player')?.getAttribute('data-dim');
  return dim === 'D3' ? 'translateX(-50%)' : '';
}

function applySheetY(sheet: HTMLElement, dy: number, base: string) {
  const y = Math.max(0, dy);
  sheet.style.transform = base ? `${base} translateY(${y}px)` : `translateY(${y}px)`;
  sheet.style.opacity = String(Math.max(0.45, 1 - y / 420));
}

/**
 * Bottom-sheet grabber: drag down (or tap) to dismiss.
 */
export function SheetHandle({
  onDismiss,
  className = 'panel-sheet__handle',
  testId = 'sheet-handle',
}: SheetHandleProps) {
  const { t } = useI18n();
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    sheet: HTMLElement;
    base: string;
    moved: boolean;
  } | null>(null);

  const clearDragStyles = (sheet: HTMLElement) => {
    sheet.style.transition = '';
    sheet.style.transform = '';
    sheet.style.opacity = '';
    sheet.style.animation = '';
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    const sheet = event.currentTarget.closest(SHEET_ROOT);
    if (!(sheet instanceof HTMLElement)) return;

    event.preventDefault();
    const base = sheetBaseTransform(sheet);
    sheet.style.animation = 'none';
    sheet.style.transition = 'none';
    applySheetY(sheet, 0, base);

    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      sheet,
      base,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dy) > 4) drag.moved = true;
    applySheetY(drag.sheet, dy, drag.base);
  };

  const finish = (event: ReactPointerEvent<HTMLButtonElement>, cancelled: boolean) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    const dy = Math.max(0, event.clientY - drag.startY);
    const { sheet, base, moved } = drag;

    // Tap (no meaningful drag) or pull past threshold → dismiss.
    if (!cancelled && ((!moved && dy < 8) || dy >= DISMISS_PX)) {
      sheet.style.transition = 'transform 0.18s ease-out, opacity 0.18s ease-out';
      applySheetY(sheet, Math.max(dy, sheet.getBoundingClientRect().height * 0.35), base);
      window.setTimeout(() => {
        clearDragStyles(sheet);
        onDismiss();
      }, 160);
      return;
    }

    sheet.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
    applySheetY(sheet, 0, base);
    window.setTimeout(() => clearDragStyles(sheet), 200);
  };

  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      aria-label={t('settingsClose')}
      title={t('settingsClose')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => finish(e, false)}
      onPointerCancel={(e) => finish(e, true)}
    />
  );
}
